import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CP, CapCard, Kpi, HBar } from "./ui";
import { Target, Database, Cloud, UsersThree } from "@phosphor-icons/react";

const PRIORITY_ICONS = [Target, Database, Cloud, UsersThree];
const heatColor = (v) => (v >= 3.5 ? "#EF4444" : v >= 2.5 ? "#F59E0B" : "#FDE68A");

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/capability/executive").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm py-10 text-center" style={{ color: CP.sub }}>Loading executive overview…</div>;
  const k = data.kpis;
  const hm = data.heatmap;

  return (
    <div className="space-y-4" data-testid="executive-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total Capabilities" value={k.total_capabilities} testid="exec-kpi-total" />
        <Kpi label="Workforce Coverage" value={`${k.workforce_coverage_pct}%`} delta={k.delta_coverage} deltaLabel="vs last quarter" testid="exec-kpi-coverage" />
        <Kpi label="Capability Maturity" value={`${k.maturity} / 5`} tone={CP.purple} delta={Math.round(k.delta_maturity * 10)} deltaLabel="vs last quarter" testid="exec-kpi-maturity" />
        <Kpi label="Business Impact" value={k.business_impact} tone={k.business_impact === "High" ? CP.red : CP.amber} delta={k.delta_impact} deltaLabel="vs last quarter" testid="exec-kpi-impact" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CapCard title="Maturity by Capability Domain" testid="exec-maturity">
          <div className="space-y-3 pt-1">
            {data.maturity_by_domain.map((d) => (
              <HBar key={d.domain} label={d.domain} value={d.score} max={5} color={CP.blue} />
            ))}
          </div>
        </CapCard>

        <CapCard title="Capability Heatmap" subtitle="Department × domain concentration" testid="exec-heatmap">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr>
                  <th />
                  {hm.domains.map((d) => (
                    <th key={d} className="pb-1.5 font-medium text-center px-1" style={{ color: CP.sub }}>{d.split(" ")[0]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hm.departments.map((dept, ri) => (
                  <tr key={dept}>
                    <td className="pr-2 py-0.5 whitespace-nowrap font-medium" style={{ color: CP.text }}>{dept}</td>
                    {hm.cells[ri].map((v, ci) => (
                      <td key={ci} className="p-0.5">
                        <div className="rounded-md h-8 flex items-center justify-center font-bold" style={{ background: heatColor(v), color: v >= 2.5 ? "#fff" : "#92400E" }} title={`${dept} × ${hm.domains[ci]}: ${v}`}>{v}</div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3 mt-2 text-[10px]" style={{ color: CP.sub }}>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: "#FDE68A" }} />Low</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: "#F59E0B" }} />Medium</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: "#EF4444" }} />High</span>
          </div>
        </CapCard>

        <CapCard title="Top Priorities" subtitle="Strategic capability moves" testid="exec-priorities">
          <div className="space-y-2">
            {data.top_priorities.map((p, i) => {
              const Icon = PRIORITY_ICONS[i % PRIORITY_ICONS.length];
              return (
                <div key={i} className="flex items-start gap-3 rounded-lg p-3" style={{ border: `1px solid ${CP.border}` }} data-testid={`exec-priority-${i}`}>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F3F0FF" }}>
                    <Icon size={16} color={CP.purple} weight="duotone" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: CP.text }}>{p.title}</div>
                    <div className="text-[11px]" style={{ color: CP.sub }}>{p.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </CapCard>
      </div>
    </div>
  );
}
